/**
 * Nested "page 2" drawers inside WebsiteEditorInspector (content picker,
 * schema child edit, options, group picker, …).
 *
 * Sticky Update pops the top nested drawer instead of closing the inspector.
 */
import React from "react";

export type InspectorNestedDrawerApi = {
  /** Register while a nested drawer is open. Returns unregister. */
  register: (id: string, close: () => void) => () => void;
  /** Close the topmost nested drawer. True if one was closed. */
  closeTop: () => boolean;
  /** Close every nested drawer (session reset / inspector commit). */
  closeAll: () => void;
  /** Current nested depth (for sticky CTA / CSS). */
  depth: number;
  /** Id of the topmost nested drawer, if one is open. */
  topId: string | null;
};

const InspectorNestedDrawerContext =
  React.createContext<InspectorNestedDrawerApi | null>(null);

export function InspectorNestedDrawerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const stackRef = React.useRef<Array<{ id: string; close: () => void }>>([]);
  const [depth, setDepth] = React.useState(0);
  const [topId, setTopId] = React.useState<string | null>(null);

  const syncDepth = React.useCallback(() => {
    setDepth(stackRef.current.length);
    setTopId(stackRef.current[stackRef.current.length - 1]?.id ?? null);
  }, []);

  const register = React.useCallback(
    (id: string, close: () => void) => {
      stackRef.current = stackRef.current.filter((entry) => entry.id !== id);
      stackRef.current.push({ id, close });
      syncDepth();
      return () => {
        stackRef.current = stackRef.current.filter((entry) => entry.id !== id);
        syncDepth();
      };
    },
    [syncDepth],
  );

  const closeTop = React.useCallback(() => {
    const top = stackRef.current.pop();
    if (!top) return false;
    syncDepth();
    top.close();
    return true;
  }, [syncDepth]);

  const closeAll = React.useCallback(() => {
    const stack = stackRef.current.splice(0, stackRef.current.length);
    syncDepth();
    // Close deepest first so UI unwind matches user expectation.
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      stack[i]?.close();
    }
  }, [syncDepth]);

  const api = React.useMemo<InspectorNestedDrawerApi>(
    () => ({ register, closeTop, closeAll, depth, topId }),
    [register, closeTop, closeAll, depth, topId],
  );

  return (
    <InspectorNestedDrawerContext.Provider value={api}>
      {children}
    </InspectorNestedDrawerContext.Provider>
  );
}

export function useInspectorNestedDrawer(): InspectorNestedDrawerApi | null {
  return React.useContext(InspectorNestedDrawerContext);
}

/** Register `close` while `open` is true. Prefer confirm/commit closers for Update. */
export function useRegisterInspectorNestedDrawer(
  id: string,
  open: boolean,
  close: () => void,
) {
  const api = useInspectorNestedDrawer();
  const closeRef = React.useRef(close);
  closeRef.current = close;

  React.useEffect(() => {
    if (!api || !open) return;
    return api.register(id, () => closeRef.current());
  }, [api, id, open]);
}
